import { useMutation, useQueryClient } from "react-query";
import { Controller, useForm } from "react-hook-form";
import axiosClient from "../../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import { useMemo } from "react";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField } from "@mui/material";
import { TConsultores } from "../../../../../../types/api/comercial/consultores";
import { TOpenModalConsultor } from "..";

export default function DialogCriacaoEditConsultores({
  open,
  handleClose,
  defaultValue,
}: DialogCriacaoEditConsultoresProps) {
  const queryClient = useQueryClient();

  //Valores iniciais
  const isEdit = useMemo(() => Boolean(defaultValue), [defaultValue])
  const valoresIniciais = useMemo(() => defaultValue?.defaultValue ? defaultValue.defaultValue : null, [defaultValue])
  //

  const { control, handleSubmit } = useForm<TConsultores>({
    defaultValues: {
      id: valoresIniciais?.id,
      nome: valoresIniciais?.nome || "",
    }
  });

  //Querys e mutations
  const { mutate: criarConsultor, isLoading: isLoadingRegister } = useMutation({
    mutationKey: "criar-consultor",
    mutationFn: (inputs: TConsultores) =>
      axiosClient.post(
        "comercial/configuracoes/criar-consultor",
        inputs
      ),
    onError: (error) => {
      console.error(error);
      enqueueSnackbar("Erro ao cadastrar consultor!", {
        variant: "error",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["pesquisar-consultores"]);
      handleClose();
      enqueueSnackbar("Consultor cadastrado com sucesso!", {
        variant: "success",
      });
    },
  });

  const { mutate: editarConsultor, isLoading: isLoadingUpdate } = useMutation({
    mutationKey: "editar-consultor",
    mutationFn: (inputs: TConsultores) =>
      axiosClient.put(
        `comercial/configuracoes/editar-consultor/${valoresIniciais?.id}`,
        inputs
      ),
    onError: (error) => {
      console.error(error);
      enqueueSnackbar("Erro ao atualizar consultor!", {
        variant: "error",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["pesquisar-consultores"]);
      handleClose();
      enqueueSnackbar("Consultor atualizado com sucesso!", {
        variant: "success",
      });
    },
  });
  //

  const onSubmit = (data: TConsultores) => {
    const payload = {
      id: data.id,
      nome: data.nome,
      status: data.status
    };
    if (isEdit) {
      editarConsultor(payload);
    }
    if (!isEdit) {
      criarConsultor(payload);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>
          {isEdit ? "Editar" : "Cadastrar"} Consultor
        </DialogTitle>

        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Box px={0.5} my={1}>
              <Grid container spacing={2}>
                <Grid size={{xs: 12, sm: 12}}>
                  <Controller
                    name="nome"
                    control={control}
                    rules={{ required: "Nome é obrigatório" }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        label="Nome"
                        fullWidth
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>

          <DialogActions>
            <Button variant="outlined" onClick={handleClose}>
              Cancelar
            </Button>

            <Button
              variant="contained"
              type="submit"
              disabled={isLoadingRegister || isLoadingUpdate}
            >
              {(isLoadingRegister || isLoadingUpdate) && (
                <CircularProgress size={20} sx={{ marginRight: 1 }} />
              )}
              {isEdit ? "Atualizar" : "Cadastrar"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}

interface DialogCriacaoEditConsultoresProps {
  open: boolean;
  handleClose: () => void;
  defaultValue?: TOpenModalConsultor | null;
}
